import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as api from "../api/MeetingsApi";
import type { Meeting, MeetingEmployeeOption, MeetingFilters, MeetingPayload, MeetingSummary } from "../api/MeetingsApi";
import { VerifyContext } from "../../../app/VerifyContext";

export const PAGE_SIZE = 20;

export interface MeetingsFilterState {
  search: string;
  employeeId: number | null;
  conductorRole: string;
  dateFrom: string;
  dateTo: string;
}

export const EMPTY_FILTERS: MeetingsFilterState = {
  search: "",
  employeeId: null,
  conductorRole: "",
  dateFrom: "",
  dateTo: "",
};

interface MeetingsContextValue {
  employees: MeetingEmployeeOption[];
  employeesLoading: boolean;
  meetings: Meeting[];
  meetingsLoading: boolean;
  summary: MeetingSummary | null;
  error: string;
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
  filters: MeetingsFilterState;
  canView: boolean;
  canEdit: boolean;

  setFilters: (patch: Partial<MeetingsFilterState>) => void;
  resetFilters: () => void;
  setPage: (page: number) => void;
  loadMeetings: () => Promise<void>;

  createMeeting: (data: MeetingPayload) => Promise<void>;
  updateMeeting: (id: number, data: Partial<MeetingPayload>) => Promise<void>;
  deleteMeeting: (id: number) => Promise<void>;
}

const MeetingsContext = createContext<MeetingsContextValue | null>(null);

export function MeetingsProvider({ children }: { children: React.ReactNode }) {
  const { superAdmin, effectivePermissions } = useContext(VerifyContext);
  const canView = superAdmin || !!effectivePermissions["meetings"]?.can_view;
  const canEdit = superAdmin || !!effectivePermissions["meetings"]?.can_edit;

  const [employees, setEmployees] = useState<MeetingEmployeeOption[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [meetingsLoading, setMeetingsLoading] = useState(false);
  const [summary, setSummary] = useState<MeetingSummary | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  // Sequence numbers so a slow, older response never overwrites a newer one.
  const meetingsSeq = useRef(0);
  const employeesSeq = useRef(0);
  const [filters, setFiltersState] = useState<MeetingsFilterState>(EMPTY_FILTERS);

  const setFilters = useCallback((patch: Partial<MeetingsFilterState>) => {
    setFiltersState((prev) => ({ ...prev, ...patch }));
    setPage(1); // any filter change restarts from the first page
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(EMPTY_FILTERS);
    setPage(1);
  }, []);

  const loadEmployees = useCallback(async () => {
    if (!canView) return;
    const seq = ++employeesSeq.current;
    setEmployeesLoading(true);
    try {
      const data = await api.listMeetingEmployees();
      if (seq === employeesSeq.current) setEmployees(data);
    } catch {
      if (seq === employeesSeq.current) setError("Failed to load employees");
    } finally {
      if (seq === employeesSeq.current) setEmployeesLoading(false);
    }
  }, [canView]);

  const loadMeetings = useCallback(async () => {
    if (!canView) return;
    const seq = ++meetingsSeq.current;
    setMeetingsLoading(true);
    try {
      const f: MeetingFilters = {
        page,
        page_size: PAGE_SIZE,
        employee_id: filters.employeeId ?? undefined,
        conductor_role: filters.conductorRole || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        search: filters.search || undefined,
      };
      const data = await api.listMeetings(f);
      if (seq !== meetingsSeq.current) return;
      // Deleting the last row of the last page leaves this page empty: step back.
      if (data.items.length === 0 && page > data.total_pages) {
        setPage(data.total_pages);
        return;
      }
      setMeetings(data.items);
      setSummary(data.summary);
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);
    } catch {
      if (seq === meetingsSeq.current) setError("Failed to load meeting records");
    } finally {
      if (seq === meetingsSeq.current) setMeetingsLoading(false);
    }
  }, [canView, page, filters]);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { loadMeetings(); }, [loadMeetings]);

  const createMeeting = useCallback(async (data: MeetingPayload) => {
    await api.createMeeting(data);
    // Newest meetings sort first, so jump back to page 1 to show the new record.
    if (page !== 1) setPage(1);
    else await loadMeetings();
    await loadEmployees();
  }, [page, loadMeetings, loadEmployees]);

  const updateMeeting = useCallback(async (id: number, data: Partial<MeetingPayload>) => {
    await api.updateMeeting(id, data);
    await loadMeetings();
    await loadEmployees(); // an edited date changes the employee's "last meeting"
  }, [loadMeetings, loadEmployees]);

  const deleteMeeting = useCallback(async (id: number) => {
    await api.deleteMeeting(id);
    await loadMeetings();
    await loadEmployees();
  }, [loadMeetings, loadEmployees]);

  const value = useMemo<MeetingsContextValue>(() => ({
    employees, employeesLoading, meetings, meetingsLoading, summary, error,
    totalCount, totalPages, page, pageSize: PAGE_SIZE, filters,
    canView, canEdit,
    setFilters, resetFilters, setPage, loadMeetings,
    createMeeting, updateMeeting, deleteMeeting,
  }), [
    employees, employeesLoading, meetings, meetingsLoading, summary, error,
    totalCount, totalPages, page, filters, canView, canEdit,
    setFilters, resetFilters, loadMeetings, createMeeting, updateMeeting, deleteMeeting,
  ]);

  return <MeetingsContext.Provider value={value}>{children}</MeetingsContext.Provider>;
}

export function useMeetings(): MeetingsContextValue {
  const ctx = useContext(MeetingsContext);
  if (!ctx) throw new Error("useMeetings must be used within MeetingsProvider");
  return ctx;
}
